import {
  LoadingIndicator,
  SearchBox,
  Checkbox,
  IconButton,
  Icon,
} from '@monash/portal-react';
import { useContext, useState, useMemo, useRef, useCallback } from 'react';
import { SlideOutContext } from '../../common/slide-out/SlideOutWrapper';
import cs from '../shared/links-shared.module.scss';
import LinkForm from '../link-form/LinkForm';
import { useApi, getLinksArchive } from '@monash/portal-frontend-common';
import BulkActionsModal from '../shared/bulk-actions-modal/BulkActionsModal';
import * as Sentry from '@sentry/browser';
import { useEnrichedLinks } from '../shared/hooks/use-enriched-links';
import { bySearch } from '../shared/utils/by-search';
import { byLatestSave } from '../shared/utils/by-latest-save';
import { useLinksState } from '../shared/hooks/use-links-state';
import { useCheckedLinks } from '../shared/hooks/use-checked-links';
import ExpandableLinkCard from '../shared/expandable-link-card/ExpandableLinkCard';
import { categoriesStore } from '../shared/stores';
import { getAllCategories } from '@monash/portal-frontend-common/dist/api/api';
import { useStore } from '@longsien/react-store';
import {
  getCategoryFilterOptions,
  getCohortFilterOptions,
} from '../shared/utils/get-filter-options';
import { byCategory } from '../shared/utils/by-category';
import { byCohort } from '../shared/utils/by-cohort';
import CategoryFiltersTags from '../shared/filters/filters-tags/category-filters-tags/CategoryFiltersTags';
import CohortFiltersTags from '../shared/filters/filters-tags/cohort-filters-tags/CohortFiltersTags';
import FiltersButton from '../shared/filters/filters-button/FiltersButton';
import FiltersModal from '../shared/filters/filters-modal/FiltersModal';
import { isDeletableFromArchive } from '../shared/utils/statuses';
import { SUBMIT_TYPES } from '../shared/constants';

const LinksArchives = () => {
  const { setSlide, open, setOpen, slide } = useContext(SlideOutContext);

  const [bulkActionsModalShown, setBulkActionsModalShown] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const bulkActionTriggerRef = useRef();

  const [categories, setCategories] = useStore(categoriesStore);

  useApi({
    requestFn: getAllCategories,
    onSuccess: (response) => {
      setCategories(response);
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Categories data has failed to load.');
    },
  });

  const [filtersModalShown, setFiltersModalShown] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCohorts, setSelectedCohorts] = useState([]);

  const categoryFilterOptions = getCategoryFilterOptions(categories);
  const cohortFilterOptions = getCohortFilterOptions();

  const { links, setLinks, deleteLink } = useLinksState();

  const { isLoading: isLoadingLinks } = useApi({
    requestFn: getLinksArchive,
    onSuccess: (response) => {
      setLinks(response);
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Links data has failed to load.');
    },
  });

  const enrichedLinks = useEnrichedLinks({ links, categories });

  const [searchTarget, setSearchTarget] = useState('');

  const processedLinks = useMemo(() => {
    return enrichedLinks
      .filter(bySearch(searchTarget))
      .filter(byCategory(selectedCategories))
      .filter(byCohort(selectedCohorts))
      .sort(byLatestSave);
  }, [enrichedLinks, searchTarget, selectedCategories, selectedCohorts]);

  const {
    checkedLinks,
    handleCheck,
    handleCheckAll,
    updateChecked,
    isChecked,
    isAllChecked,
  } = useCheckedLinks({ links: processedLinks });

  const openSlideForm = useCallback(
    (link) => {
      setSlide({
        content: (
          <LinkForm
            existing={link}
            deleteLink={deleteLink}
            cancel={() => setOpen(false)}
            key={link ? link.id : Math.random()} // to update form values when clicking on different link cards
          />
        ),
        data: link,
      });
      setOpen(true);
    },
    [setSlide, setOpen]
  );

  return (
    <div className={cs.container}>
      <div className={cs.allLinks}>
        <div className={cs.header}>
          <h1>Archives</h1>
        </div>
        <div>
          <SearchBox searchString={searchTarget} onInput={setSearchTarget} />
        </div>
        {isLoadingLinks ? (
          <div className={cs.loading}>
            <LoadingIndicator />
          </div>
        ) : (
          <>
            <div className={cs.filtersWrapper}>
              <FiltersButton onClick={() => setFiltersModalShown(true)} />
              <CategoryFiltersTags
                options={categoryFilterOptions}
                selectedValues={selectedCategories}
                onRemove={(v) =>
                  setSelectedCategories((prevCats) =>
                    prevCats.filter((cat) => cat !== v)
                  )
                }
              />
              <CohortFiltersTags
                options={cohortFilterOptions}
                selectedValues={selectedCohorts}
                onRemove={(v) =>
                  setSelectedCohorts((prevCohorts) =>
                    prevCohorts.filter((cohort) => cohort !== v)
                  )
                }
              />
              <FiltersModal
                open={filtersModalShown}
                onClose={() => setFiltersModalShown(false)}
                categoryFilterOptions={categoryFilterOptions}
                selectedCategories={selectedCategories}
                onChangeCategories={setSelectedCategories}
                cohortFilterOptions={cohortFilterOptions}
                selectedCohorts={selectedCohorts}
                onChangeCohorts={setSelectedCohorts}
              />
            </div>
            <div className={cs.secondaryHeader}>
              <div className={cs.secondaryHeaderActions}>
                <Checkbox on={isAllChecked} onChange={handleCheckAll} />
                {checkedLinks.some(isDeletableFromArchive) && (
                  <IconButton
                    ref={bulkActionTriggerRef}
                    icon={Icon.Trash}
                    color="var(--canvas-text-color)"
                    onClick={() => {
                      setBulkAction(SUBMIT_TYPES.DELETE_ARCHIVED);
                      setBulkActionsModalShown(true);
                    }}
                    aria-label="Bulk Delete"
                  />
                )}
              </div>

              <p>{`${processedLinks.length} ${
                processedLinks.length === 1 ? 'link' : 'links'
              }`}</p>
            </div>
            <div className={cs.linksList}>
              {processedLinks.map((link) => {
                const id = link.id;

                return (
                  <ExpandableLinkCard
                    key={id}
                    link={link}
                    selected={open && slide?.data?.id === id}
                    openSlideForm={openSlideForm}
                    checked={isChecked(id)}
                    handleCheck={handleCheck}
                  />
                );
              })}
            </div>
            <BulkActionsModal
              open={bulkActionsModalShown}
              selectedLinks={checkedLinks}
              deleteLink={deleteLink}
              updateChecked={updateChecked}
              onClose={() => {
                setBulkActionsModalShown(false);
                bulkActionTriggerRef.current?.focus();
              }}
              action={bulkAction}
              triggerRef={bulkActionTriggerRef}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default LinksArchives;
